import { ROLE_LABELS, type Role } from '@/shared/api';
import { addressLookupCopy, type AddressLookupCopy } from '@/shared/ui/address/copy';

/**
 * Teksten van scherm 11, Instellingen. Labels zijn zelfstandige naamwoorden,
 * knoppen werkwoorden, jij-vorm, geen uitroeptekens.
 *
 * De namen van de rapportinstellingen zijn dezelfde als op de klantpagina
 * (`public-report/components/AdminSidebar.tsx`): daar pas je ze per link aan,
 * hier kies je wat een nieuwe link standaard krijgt.
 */
export const settingsCopy = {
    /** Hetzelfde woord als in de rail. De huisstijl is een tabblad. */
    title: 'Instellingen',
    /** De rij tabbladen in de kop, voor een schermlezer. */
    tabsLabel: 'Instellingen',
    loading: 'Instellingen laden',
    eyebrowUsers: (count: number) => `${count} ${count === 1 ? 'gebruiker' : 'gebruikers'}`,
    readOnly: 'Alleen de eigenaar past dit aan.',
    failed: 'Dat lukte niet. Probeer het opnieuw.',
    saved: 'Opgeslagen.',
    cancel: 'Annuleren',
} as const;

/**
 * De tabbladen van Instellingen: het woord in de kop, en de ene zin boven de
 * kaarten die zegt wat je daar regelt. Een zin noemt alleen wat er voor jou
 * staat: zonder `settings.manage` is er geen tegoed.
 */
export const settingsTabsCopy = {
    company: {
        label: 'Bedrijf',
        intro: 'Je bedrijfsgegevens op rapporten, facturen en in berichten.',
        introWithCredits:
            'Je bedrijfsgegevens op rapporten, facturen en in berichten, en wat er gebeurt als het tegoed opraakt.',
    },
    branding: {
        label: 'Huisstijl',
        intro: 'Je logo en kleuren in het rapport, je e-mails en op de klantpagina, met een voorbeeld van wat je klant ziet.',
    },
    report: {
        label: 'Rapport',
        intro: 'Wat een nieuw rapport en zijn link standaard meekrijgen, en wat Smartkeur vanzelf voor je doet.',
    },
    messages: {
        label: 'Berichten',
        intro: 'Wat klanten vanzelf van je krijgen, en via welke mailserver dat gaat.',
    },
    team: {
        label: 'Team',
        intro: 'Wie er bij je bedrijf werkt, met welke rol, en wie er nog is uitgenodigd.',
    },
    domains: {
        label: 'Domeinen',
        intro: 'Waar klanten je rapporten openen: op je eigen adres of op dat van Smartkeur.',
    },
    integrations: {
        label: 'Koppelingen',
        intro: 'Hier koppel je Smartkeur aan je eigen software of aan een AI-assistent zoals Claude of ChatGPT, zodat die bijvoorbeeld keuringen voor je opzoekt of inplant. Dit is helemaal optioneel: zonder koppeling werkt Smartkeur precies hetzelfde.',
    },
} as const;

export const brandingCopy = {
    title: 'Huisstijl in rapporten',
    note: 'Klanten zien jouw naam, logo en kleuren — Smartkeur blijft onzichtbaar.',
    logo: 'Logo',
    logoDropzone: 'sleep logo',
    logoUpload: 'Logo uploaden',
    logoAlt: (company: string) => `Logo van ${company}`,
    logoFormats:
        'PNG, JPG of WebP. Een SVG-bestand kan niet. Een PNG met een doorzichtige achtergrond blijft doorzichtig.',
    logoWrongType: 'Dit bestand kan niet als logo. Kies een PNG-, JPG- of WebP-bestand.',
    logoUploading: 'Logo wordt opgeslagen…',
    logoSaved: 'Het nieuwe logo staat in je rapporten.',
    logoRemove: 'Logo verwijderen',
    logoRemoved: 'Het logo is weggehaald. Rapporten tonen nu je bedrijfsnaam.',

    accent: 'Accentkleur',
    accentHint:
        'De lijn op het voorblad, de streep boven een hoofdstuk, de knop in je e-mails en de links op de klantpagina.',
    accentOwn: 'Eigen kleur',
    accentText: 'Tekst op de accentkleur',
    accentTextHint: 'Wat er in een gekleurde knop of balk staat.',
    heading: 'Kleur van koppen',
    headingHint:
        'De rustige tweede kleur voor koppen en balken in het rapport. Laat je hem leeg, dan blijven koppen donkergrijs.',

    /** Elke kleurrij: de kiezer met het hele spectrum en het veld voor een eigen code. */
    pick: (what: string) => `${what} kiezen`,
    code: (what: string) => `Kleurcode voor ${what.toLowerCase()}`,
    codePlaceholder: '#1E5EFF',
    codeInvalid: 'Dat is geen kleurcode. Gebruik bijvoorbeeld #1E5EFF of rgb(30, 94, 255).',
    automatic: 'Automatisch',
    automaticOn: (colour: string) => `Automatisch: ${colour}`,
    chooseOwn: 'Zelf kiezen',
    white: 'wit',
    ink: 'donkergrijs',
    standard: 'Standaard',
    standardOn: 'Standaard: donkergrijs',

    /** `8.42` → `Contrast 8,4:1 — goed leesbaar`. */
    contrast: (ratio: number) =>
        `Contrast ${ratio.toFixed(1).replace('.', ',')}:1 — goed leesbaar.`,
    contrastTooLow: (ratio: number) =>
        `Contrast ${ratio.toFixed(1).replace('.', ',')}:1 — te weinig om te lezen. We drukken dan zelf een leesbare kleur af.`,
    contrastFix: 'Maak leesbaar',

    statusNote:
        'De kleuren van de oordelen (goed, voldoende, matig, onvoldoende, niet waarneembaar) en de amber van offline blijven zoals ze zijn: die zeggen wat iets is.',
    save: 'Kleuren opslaan',
    saving: 'Bezig met opslaan…',
    saved: 'De kleuren staan in je rapporten, je e-mails en op de klantpagina.',
} as const;

export const companyCopy = {
    title: 'Bedrijfsgegevens',
    intro: 'Wat klanten, rapporten en facturen over je bedrijf laten zien. Een rapport dat al gemaakt is en een verstuurde factuur houden de gegevens die ze hadden; de klantpagina toont altijd de huidige.',
    name: 'Handelsnaam',
    nameHint: 'De naam in berichten aan klanten, op het rapport en op de klantpagina.',
    nameMissing: 'Vul de handelsnaam in.',
    legalName: 'Statutaire naam',
    legalNameHint:
        'Op de factuur, onderaan berichten aan klanten en in de NHG-bijlage. Leeg: de handelsnaam.',
    address: 'Adres',
    addressHint:
        'Bovenaan het rapport en de klantpagina, op de factuur, onderaan berichten aan klanten en in de NHG-bijlage.',
    addressIncomplete: 'Maak het adres af, of maak de velden leeg.',
    phone: 'Telefoon',
    phoneHint:
        'Bovenaan het rapport en de klantpagina, in berichten aan klanten en in de NHG-bijlage.',
    email: 'E-mailadres',
    emailHint: 'Bovenaan het rapport en de klantpagina, in berichten aan klanten en op de factuur.',
    website: 'Website',
    websiteHint: 'Bovenaan het rapport en de klantpagina, en onderaan de e-mails aan klanten.',
    kvk: 'KvK-nummer',
    kvkHint: 'Op de factuur, in het rapport en in de NHG-bijlage.',
    vat: 'Btw-nummer',
    vatHint: 'Op de factuur.',
    iban: 'IBAN',
    ibanHint: 'Op de factuur, zodat klanten kunnen overmaken.',
    registeredAsBefore: 'Als wat je bedrijf in de NHG-bijlage staat ingeschreven, kies je bij ',
    registeredAsLink: 'NHG-bijlage',
    registeredAsAfter: '.',
    empty: 'Niet ingevuld',
    save: 'Bedrijfsgegevens opslaan',
    saving: 'Bezig met opslaan…',
    saved: 'De bedrijfsgegevens zijn opgeslagen.',
} as const;

/** Het adres van het bedrijf opzoeken in de BAG: straat en plaats, of zelf invullen. */
export const companyAddressCopy: AddressLookupCopy = {
    ...addressLookupCopy,
    hint: 'Straat en plaats komen uit de BAG zodra postcode en huisnummer er staan.',
    choose: (count) => `Op dit huisnummer staan ${count} adressen. Kies het adres van je bedrijf.`,
};

export const coverCopy = {
    title: 'Voorbeeld',
    reportTab: 'Rapport',
    clientTab: 'Klantpagina',
    /** Zoals op het voorblad: de soort rapport met de norm. */
    eyebrow: 'Bouwkundig keuringsrapport · NTA 8060',
    heading: 'Samenvatting',
    sampleAddress: 'Kerkweg 41, Barendrecht',
    clientIntro: 'Uw keuringsrapport staat klaar.',
    clientButton: 'Rapport bekijken',
    /** Een oordeel zoals het in het rapport staat: altijd in de kleur van de keuring. */
    sampleJudgement: 'Voldoende',
    clientLink: 'Kosten en adviezen',
    note: 'Zo ziet je klant het rapport en de klantpagina. De app zelf houdt de kleuren van Smartkeur.',
} as const;

/**
 * De eerste zin onder "Factuur automatisch meesturen" en "Herinneringen versturen".
 * De schakelaars staan alleen onder Automatisering (tabblad Rapport); Berichten
 * toont dezelfde zin als stand, met een link naar de schakelaar.
 */
const invoiceWithReportState = {
    on: 'Aan: gaat het rapport vanzelf naar de klant, dan zit de factuur in dezelfde mail.',
    manual: 'Aan, maar je verstuurt rapporten zelf: dan kies je bij het versturen of de factuur meegaat.',
    off: 'Uit: een rapport dat vanzelf gaat, gaat zonder factuur.',
} as const;

/** `op de ochtend van de afspraak` of `2 dagen voor de afspraak, op hetzelfde tijdstip,`. */
function reminderMoment(days: number): string {
    return days === 0
        ? 'op de ochtend van de afspraak'
        : `${days} ${days === 1 ? 'dag' : 'dagen'} voor de afspraak, op hetzelfde tijdstip,`;
}

const remindersOff = 'Uit: klanten krijgen geen herinnering aan hun afspraak.';

export const automationCopy = {
    title: 'Automatisering',
    autoSend: 'Rapport automatisch versturen',
    autoSendOn:
        'Zodra het rapport klaar is, gaat het naar de klant — zonder dat de eigenaar het naleest.',
    autoSendOff:
        'Uit: de eigenaar leest het rapport eerst na. Goedkeuren is versturen: de klant krijgt het rapport op het moment van goedkeuren.',

    invoiceWithReport: 'Factuur automatisch meesturen met het rapport',
    invoiceWithReportOn: `${invoiceWithReportState.on} De factuur gaat mee als pdf, met je standaard eigen bericht uit Berichten. Is er nog geen factuur, dan maakt Smartkeur haar uit de diensten van de keuring; een concept wordt definitief met het volgende factuurnummer. Een factuur die de klant al heeft, gaat niet nog een keer mee.`,
    invoiceWithReportManual: `${invoiceWithReportState.manual} Zet Rapport automatisch versturen aan om de factuur vanzelf mee te laten gaan.`,
    invoiceWithReportOff: `${invoiceWithReportState.off} Verstuur je zelf, dan kies je het per keer.`,

    autoInvoice: 'Automatisch factureren',
    autoInvoiceOn:
        'Zodra het rapport naar de klant is verstuurd, maakt Smartkeur een conceptfactuur voor die keuring, met de prijs die bij de keuring is afgesproken. Het concept staat bij Facturatie: het krijgt pas een nummer en gaat pas naar de klant als de eigenaar het verstuurt. Een keuring krijgt nooit twee facturen.',
    autoInvoiceOff: 'Uit: als het rapport verstuurd is, ontstaat er geen factuur.',

    reminders: 'Herinneringen versturen',
    remindersOn: (moment: string) =>
        `Aan: de klant krijgt ${moment} een herinnering. Met de datum, het tijdstip, het adres en de naam van de inspecteur: per mail, en per sms als er een telefoonnummer bekend is. Eén keer per afspraak; verplaats je de afspraak, dan volgt er één voor het nieuwe tijdstip. Tussen 21:00 en 08:00 gaat er niets uit.`,
    remindersOff,
    reminderMoment,
    reminderDays: 'Herinnering sturen',
    reminderOption: (days: number) =>
        days === 0 ? 'Op de dag zelf' : `${days} ${days === 1 ? 'dag' : 'dagen'} van tevoren`,
} as const;

export const creditsCopy = {
    title: 'Tegoed',
    intro: 'Wat er gebeurt als je keuringen op zijn, en vanaf wanneer de app waarschuwt. Tegoed telt in keuringen.',
    /* De regels zet Smartkeur; de eigenaar leest ze hier als uitleg. */
    ruleBlock: 'Zijn je keuringen op, dan kun je pas weer nieuwe maken na een aankoop.',
    ruleGrace: (n: number) =>
        `Smartkeur geeft je een ruimte onder nul van ${n} ${n === 1 ? 'keuring' : 'keuringen'}, zodat een lopende afspraak niet strandt.`,
    ruleNoBlock:
        'Smartkeur heeft voor jullie ingesteld dat nieuwe keuringen ook doorgaan als je keuringen op zijn; het saldo gaat dan onder nul.',
    ruleSetBy: 'Dit regelt Smartkeur. Moet het voor jullie anders, neem dan contact op.',
    threshold: 'Waarschuwen vanaf',
    thresholdHint: (max: number) =>
        `Heb je dit aantal keuringen of minder, dan meldt de app dat ze bijna op zijn. 0 tot ${max}.`,
    unit: 'keuringen',
    invalid: (max: number) => `Vul een heel aantal keuringen in, van 0 tot ${max}.`,
    save: 'Waarschuwing opslaan',
    saving: 'Bezig met opslaan…',
    saved: 'De waarschuwing is opgeslagen.',
} as const;

export const reportCopy = {
    title: 'Rapportinstellingen',
    intro: 'Wat een nieuwe rapportlink standaard krijgt. Per link pas je het aan op de klantpagina van die keuring.',
    costs: 'Kosten tonen aan de klant',
    costsDescription: 'De klant ziet de herstelkosten: direct, op termijn en verbetering.',
    forwarding: 'Klant mag zelf doorsturen',
    forwardingDescription: 'De klant mag de link doorsturen naar makelaar of adviseur.',
    accessCode: 'Toegang met extra code',
    accessCodeDescription: 'Naast de link is een code nodig om het rapport te openen.',
    validity: 'Geldigheid van de rapportlink',
    validityUntil: (period: string, date: string) =>
        `Een link die je vandaag deelt, werkt ${period}: tot en met ${date}. Daarna ziet de klant dat de link verlopen is en vraagt hij een nieuwe aan.`,
    /* Geen "server", "dienst" of "beheerder": een keuringsbedrijf heeft geen server. */
    pdfSetupTitle: 'De PDF kan nu niet gemaakt worden',
    pdfSetupBody: (waiting: number) =>
        `Smartkeur lost dit op; daarna maken we de PDF's vanzelf.${waiting > 0 ? ` ${capitalize(pdfWaiting(waiting))}.` : ''}`,
    pdfWaitingTitle: "Nog niet alle PDF's zijn gemaakt",
    pdfWaitingBody: (waiting: number) =>
        `${capitalize(pdfWaiting(waiting))}. We proberen het vanzelf opnieuw.`,
} as const;

function pdfWaiting(count: number): string {
    return count === 1
        ? '1 verstuurd rapport wacht op zijn PDF'
        : `${count} verstuurde rapporten wachten op hun PDF`;
}

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

export const nhgCopy = {
    title: 'NHG-bijlage',
    intro: 'De bijlage van het NHG-model bij het rapport van een bouwkundige keuring: per onderdeel van de woning wat direct en wat op termijn noodzakelijk is.',
    include: 'NHG-bijlage in het rapport',
    includeOn:
        'Aan: een nieuw rapport krijgt de bijlage, in de pdf, op het rapportscherm en op de klantpagina als de klant de kosten ziet. Behalve als de vragenlijst de bijlage zelf uitzet.',
    includeOff: 'Uit: een nieuw rapport heeft geen NHG-bijlage. De kosten staan er nog wel in.',
    unchanged: 'Een goedgekeurd rapport blijft zoals het is.',
    registeredAs: 'Ingeschreven als',
    registeredAsDescription: 'Het vakje dat de bijlage aankruist bij de keuringsinstantie.',
    registeredAsNone: 'Niet ingevuld',
    registeredAsEmpty: 'Niet ingevuld: de vier vakjes in de bijlage blijven leeg.',
} as const;

/**
 * Achtergrond bij de bedragen: standaard ingeklapt, want je hebt het niet nodig om
 * te beginnen. Geen importgegevens (oude versienummers, inleesdatum, bron): die
 * zeggen een keuringsbedrijf niets.
 */
export const costSourcesCopy = {
    title: 'Achtergrond: bedragen in de vragenlijsten',
    intro: 'Elk bedrag in een rapport komt uit een antwoord in de vragenlijst. Open dit als je wilt weten hoe dat per vragenlijst zit.',
    readOnly: 'Alleen om in te zien. Het bedrag van één keuring pas je aan op het rapportscherm.',
    loading: 'Bedragen van de vragenlijsten laden',
    empty: 'Er is nog geen vragenlijst in gebruik.',
    version: (version: number) => `Versie ${version}`,
    optionsWithAmounts: 'Antwoorden met een bedrag',
    unitPrice: 'Prijs per eenheid',
    unitPriceNote: 'keer de hoeveelheid',
    entered: 'Zelf invullen',
    fixed: 'Vast bedrag',
    range: 'Laagste – hoogste',
    none: '–',
    byUrgency: (direct: string, term: string, improvement: string) =>
        `Direct ${direct} · Op termijn ${term} · Verbetering ${improvement}`,
    costGroups: 'Kostengroepen',
    costGroupNone: 'Zonder groep',
    nhg: 'NHG-onderdelen',
    nhgUsed: (used: number, total: number) => `${used} van de ${total} gebruikt.`,
    nhgWithoutCode: (count: string, one: boolean) =>
        one
            ? `${count} antwoord met een bedrag heeft geen eigen NHG-onderdeel. Het telt mee onder het onderdeel van zijn hoofdstuk; heeft dat er geen, dan kies je er een op het rapportscherm.`
            : `${count} antwoorden met een bedrag hebben geen eigen NHG-onderdeel. Ze tellen mee onder het onderdeel van hun hoofdstuk; heeft dat er geen, dan kies je er een op het rapportscherm.`,
    notRecordedTitle: 'Niet vastgelegd in de vragenlijst',
    priceLevelMissing: 'Uit welk jaar de prijzen komen (prijspeil).',
    vatMissing: 'Of de bedragen inclusief btw zijn. De NHG-bijlage vraagt bedragen inclusief btw.',
    priceLevel: (level: string) => `Prijspeil: ${level}.`,
    vatIncluded: 'Bedragen inclusief btw.',
    vatExcluded: 'Bedragen exclusief btw. De NHG-bijlage vraagt bedragen inclusief btw.',
} as const;

export const mailCopy = {
    title: 'Eigen mailserver',
    intro: 'Mail naar klanten gaat standaard via Smartkeur, met jouw bedrijfsnaam als afzender. Met een eigen server komt alles van je eigen adres.',
    ownerOnly: 'Alleen de eigenaar ziet en wijzigt de mailserver: er staan inloggegevens in.',
    statusPlatform: 'Via Smartkeur',
    statusVerified: 'Eigen server actief',
    statusUnverified: 'Nog niet getest',
    fallback:
        'Zolang een testbericht niet via je eigen server is aangekomen, gaat alle mail via Smartkeur, met jouw bedrijfsnaam als afzender.',
    verifiedOn: (date: string) => `Getest op ${date}. Mail gaat via je eigen server.`,
    lastError: (error: string) => `Laatste fout van je server: ${error}`,

    driver: 'Verzendmethode',
    host: 'Server',
    hostPlaceholder: 'smtp.jouwbedrijf.nl',
    region: 'Regio',
    regionPlaceholder: 'eu-west-1',
    port: 'Poort',
    username: 'Gebruikersnaam',
    accessKey: 'Toegangssleutel',
    password: 'Wachtwoord',
    secretKey: 'Geheime sleutel',
    serverKey: 'Serversleutel',
    passwordKeep: 'Laat leeg om het bewaarde wachtwoord te houden.',
    encryption: 'Beveiliging',
    fromName: 'Afzendernaam',
    fromAddress: 'Afzenderadres',
    replyTo: 'Antwoordadres',
    replyToHint: 'Waar antwoorden van klanten heen gaan. Leeg: het afzenderadres.',
    save: 'Mailserver opslaan',
    saving: 'Bezig met opslaan…',
    saved: 'De mailserver is opgeslagen. Stuur een testbericht om hem te activeren.',

    testTitle: 'Testbericht',
    testRecipient: 'Versturen naar',
    test: 'Testbericht versturen',
    testing: 'Bezig met versturen…',
    testOwnServer: (recipient: string) =>
        `Testbericht via je eigen server verstuurd naar ${recipient}. Je eigen afzender is nu actief.`,
    testPlatform: (recipient: string) =>
        `Testbericht via Smartkeur verstuurd naar ${recipient}, niet via een eigen server.`,
    testFailed: (error: string) => `Het testbericht kwam niet aan. De server zei: ${error}`,
    testFailedUnknown: 'Het testbericht kwam niet aan.',
} as const;

export const domainsCopy = {
    title: 'Eigen domeinen',
    intro: 'Rapportlinks en de app op je eigen adres, bijvoorbeeld rapport.jouwbedrijf.nl.',
    empty: 'Nog geen eigen domein. Rapportlinks draaien op het domein van Smartkeur.',
    primary: 'Hoofddomein',
    verified: 'Geverifieerd',
    pending: 'Nog niet geverifieerd',
    certificate: (status: string) => `Certificaat: ${status.toLowerCase()}`,
    instructions:
        'Eerst laten we zien dat dit domein van jou is. Daarvoor zet je één regel (een TXT-record) bij het bedrijf waar je domeinnaam staat, je domeinprovider. Doet iemand anders je website? Kopieer de instructie en stuur die naar hem of haar. Daarna klik je op Opnieuw controleren.',
    txtTitle: 'TXT-record bij je domeinprovider',
    /** Onder de naam: wat bij veel providers anders moet, en het alternatief. */
    txtNameHint:
        'Vraagt je provider alleen het deel vóór je domeinnaam, vul dan dat in. Lukt een record op deze naam niet, dan mag dezelfde waarde ook als TXT-record op je hoofddomein.',
    txtName: 'Naam',
    txtValue: 'Waarde',
    fileTitle: 'Of, als je dit adres zelf al host, een bestand op die website',
    fileAddress: 'Adres',
    fileContent: 'Inhoud',
    verify: 'Opnieuw controleren',
    verifying: 'Bezig met controleren…',
    verifiedNow: (hostname: string) => `${hostname} is geverifieerd.`,
    verifyFailed: (hostname: string) =>
        `Nog niet gevonden op ${hostname}. Een wijziging in DNS kan een paar uur duren.`,
    observed: (values: string) => `Wat we wel vonden: ${values}`,
    makePrimary: 'Hoofddomein maken',
    madePrimary: (hostname: string) => `${hostname} is nu het hoofddomein.`,
    remove: 'Verwijderen',
    removeLabel: (hostname: string) => `Bevestig verwijderen van ${hostname}`,
    removeBody: (hostname: string) =>
        `${hostname} wordt losgekoppeld. Links op dit domein werken daarna niet meer; nieuwe links komen op het hoofddomein of op het domein van Smartkeur.`,
    removeYes: 'Ja, domein verwijderen',
    removed: (hostname: string) => `${hostname} is verwijderd.`,

    addTitle: 'Domein toevoegen',
    hostname: 'Domeinnaam',
    hostnamePlaceholder: 'rapport.jouwbedrijf.nl',
    hostnameRequired: 'Vul een domeinnaam in.',
    type: 'Gebruiken voor',
    add: 'Domein toevoegen',
    adding: 'Bezig met toevoegen…',
    added: (hostname: string) => `${hostname} is toegevoegd. Zet nu het record klaar.`,

    /* Na de controle: zo gaat het domein verder, tot je rapportlinks erover lopen. */
    setupStepsLabel: (hostname: string) => `Voortgang van ${hostname}`,
    setupSteps: {
        verification: 'Eigendom bevestigd',
        server: 'Toegevoegd aan onze server',
        dns: 'Je adres wijst naar ons',
        certificate: 'Beveiligd met een certificaat',
        checking: 'In gebruik voor je rapportlinks',
    },
    setupStepState: { done: 'klaar', current: 'nu bezig', failed: 'gestopt', todo: 'nog niet' },
    setupBusy: {
        server: 'We voegen je domein toe aan onze server. Dat duurt meestal een paar minuten.',
        certificate:
            'We vragen een beveiligingscertificaat aan. Dat duurt meestal een paar minuten.',
        checking:
            'Alles staat klaar. We controleren nog of je rapportlinks via dit adres werken; dat gebeurt elke tien minuten.',
    },
    setupManual:
        'We zetten je domein voor je klaar; dat kan tot een werkdag duren. Daarna gaan je rapportlinks vanzelf via dit adres.',
    /** Boven het CNAME-record: wat het doet, in gewone taal. */
    dnsExplain:
        'Deze regel (een CNAME-record) stuurt bezoekers van je adres door naar Smartkeur. Je zet hem bij het bedrijf waar je domeinnaam staat, je domeinprovider. Doet iemand anders je website? Kopieer de instructie en stuur die naar hem of haar.',
    copyInstruction: 'Instructie kopiëren',
    copied: 'Gekopieerd. Stuur het naar wie je website beheert.',
    copyFailed: 'Kopiëren lukte niet. Selecteer de tekst en kopieer hem zelf.',
    /** De tekst op het klembord: voor wie de website beheert, met de records erin. */
    txtInstruction: (hostname: string, name: string, value: string) =>
        [
            `Wil je voor ${hostname} dit TXT-record zetten bij de domeinprovider? Daarmee ziet Smartkeur dat het domein van ons is.`,
            '',
            'Type: TXT',
            `Naam: ${name}`,
            `Waarde: ${value}`,
            '',
            'Vraagt de provider alleen het deel vóór de domeinnaam, vul dan dat in. Lukt een record op deze naam niet, dan mag dezelfde waarde ook als TXT-record op het hoofddomein.',
        ].join('\n'),
    cnameInstruction: (hostname: string, type: string, name: string, value: string) =>
        [
            `Wil je voor ${hostname} dit record zetten bij de domeinprovider? Het stuurt het adres door naar Smartkeur, waar onze rapporten staan.`,
            '',
            `Type: ${type}`,
            `Naam: ${name}`,
            `Waarde: ${value}`,
            '',
            'Staat er al een ander record op precies deze naam, haal dat dan eerst weg: een CNAME kan niet naast een ander record staan. Het controlerecord op _smartkeur. mag blijven.',
        ].join('\n'),
    dnsWaiting: (hostname: string) =>
        `${hostname} wijst nog niet naar ons. Zet dit record bij je domeinprovider:`,
    dnsTitle: 'CNAME-record bij je domeinprovider',
    dnsType: 'Type',
    dnsName: 'Naam',
    dnsValue: 'Waarde',
    dnsTxtNote:
        'Staat er nog een ander record op precies deze naam (bijvoorbeeld een oud controlerecord)? Haal dat eerst weg: een CNAME kan niet naast een ander record staan. Het controlerecord op _smartkeur. mag blijven.',
    nextCheck: (moment: string) => `We kijken vanzelf opnieuw, rond ${moment}.`,
    retry: 'Opnieuw proberen',
    checkNow: 'Nu opnieuw controleren',
    retrying: 'Bezig…',
    retried: (hostname: string) => `We gaan verder met ${hostname}.`,
} as const;

/** Rollen in één regel: "Eigenaar, Inspecteur". */
export function rolesText(roles: readonly Role[]): string {
    return roles.map((role) => ROLE_LABELS[role]).join(', ');
}

/** Teksten van de kaart Team: iedereen die bij dit bedrijf werkt. */
export const teamCopy = {
    title: 'Team',
    intro: 'Iedereen met toegang tot dit bedrijf, met zijn rol. Een rol die je hier kiest, geldt alleen bij dit bedrijf.',
    empty: 'Nog niemand toegevoegd aan dit bedrijf.',
    you: 'jij',
    noRole: 'Geen rol',
    memberSince: (date: string) => `Lid sinds ${date}`,
    disabledNote: 'Heeft geen toegang meer tot dit bedrijf.',

    changeRoles: 'Rol wijzigen',
    rolesLabel: (name: string) => `Rollen van ${name} in dit bedrijf`,
    rolesHint: 'Iemand kan allebei zijn: eigenaar en inspecteur.',
    rolesSave: 'Rollen opslaan',
    rolesEmpty: 'Kies minstens één rol.',
    rolesSaved: (name: string) => `De rollen van ${name} zijn bijgewerkt.`,

    disable: 'Uitschakelen',
    disableLabel: (name: string) => `Bevestig uitschakelen van ${name}`,
    disableBody: (name: string) =>
        `${name} verliest de toegang tot dit bedrijf. Het Smartkeur-account blijft bestaan, net als alle keuringen, rapporten en logregels op die naam. Werkt deze persoon ook bij een ander keuringsbedrijf, dan verandert daar niets — een bedrijf kan nooit iemands account opheffen.`,
    disableYes: 'Ja, toegang intrekken',
    disabled: (name: string) => `${name} heeft geen toegang meer tot dit bedrijf.`,
    enable: 'Weer toelaten',
    enabled: (name: string) =>
        `${name} heeft weer toegang tot dit bedrijf, met dezelfde rollen. Inloggen gaat met het eigen wachtwoord.`,
    cancel: 'Annuleren',
    lastOwner: 'Dit is de laatste eigenaar van dit bedrijf. Maak eerst iemand anders eigenaar.',

    invitationsTitle: 'Openstaande uitnodigingen',
    invitationsEmpty: 'Geen openstaande uitnodigingen.',
    invitationValid: (date: string) => `Geldig tot ${date}`,
    invitationExpired: (date: string) => `Verlopen op ${date}`,
    invitationBy: (name: string) => `uitgenodigd door ${name}`,
    resend: 'Opnieuw versturen',
    resent: (email: string, date: string) =>
        `Nieuwe uitnodiging verstuurd naar ${email}, geldig tot ${date}. De vorige link werkt niet meer.`,
    withdraw: 'Intrekken',
    withdrawLabel: (email: string) => `Bevestig intrekken van de uitnodiging voor ${email}`,
    withdrawBody: (email: string) =>
        `De link in de uitnodiging voor ${email} werkt daarna niet meer. Je kunt later opnieuw uitnodigen.`,
    withdrawYes: 'Ja, uitnodiging intrekken',
    withdrawn: (email: string) => `De uitnodiging voor ${email} is ingetrokken.`,

    inviteTitle: 'Iemand uitnodigen',
    email: 'E-mailadres',
    emailPlaceholder: 'naam@bedrijf.nl',
    emailRequired: 'Vul het e-mailadres van je collega in.',
    role: 'Rol',
    invite: 'Uitnodigen',
    inviting: 'Bezig met uitnodigen…',
    invited: (email: string, date: string) =>
        `Uitnodiging verstuurd naar ${email}. De link is geldig tot ${date}.`,
    inviteNote:
        'Heeft dit adres al een Smartkeur-account, dan komt dit bedrijf erbij en blijft het wachtwoord hetzelfde.',
    failed: 'Dat lukte niet. Probeer het opnieuw.',
} as const;

/**
 * Teksten van de kaart Berichten aan klanten en de editor erachter.
 *
 * De berichten zelf spreken de klant met u aan; alles eromheen is voor de
 * eigenaar en dus jij-vorm. `when` zegt per bericht wanneer het uitgaat, in de
 * volgorde waarin een klant ze krijgt. Een bericht dat hier niet staat, laat het
 * scherm niet zien: dan weet het niet wanneer het uitgaat.
 */
export const clientMessagesCopy = {
    title: 'Berichten aan klanten',
    intro: 'Wat klanten automatisch van je krijgen, in jouw huisstijl en met jouw naam als afzender. Houd de standaardtekst of schrijf je eigen tekst.',
    loading: 'Berichten laden',
    edit: 'Bewerken',
    editLabel: (label: string) => `${label} bewerken`,
    customised: 'Eigen tekst',
    standard: 'Standaardtekst',
    channelsLabel: 'Kanalen',

    when: {
        order_confirmed: 'Als een nieuwe keuring wordt bevestigd.',
        inspection_scheduled:
            'Als een keuring voor het eerst een afspraak krijgt, met datum, tijd en inspecteur. De sms alleen als er een telefoonnummer is.',
        inspection_rescheduled:
            'Als de afspraak naar een ander moment gaat dan de klant eerder hoorde. De sms alleen als er een telefoonnummer is.',
        appointment_reminder:
            'Voor de afspraak, als herinneringen aan staan. De sms alleen als er een telefoonnummer is.',
        order_cancelled:
            'Als een keuring wordt geannuleerd. De sms alleen als er nog een afspraak gepland stond.',
        report_ready:
            'Als het rapport naar de klant gaat, en als de klant de link doorstuurt. De sms alleen als er een telefoonnummer is.',
        report_ready_with_code:
            'In plaats van Rapport gereed als de rapportlink een toegangscode vraagt en de klant een telefoonnummer heeft. De code gaat apart per sms.',
        report_ready_with_invoice:
            'In plaats van Rapport gereed als de factuur meegaat: als je bij versturen "Factuur meesturen" aan laat. Alleen per e-mail en alleen aan de klant, met de factuur als pdf-bijlage en je eigen bericht.',
        report_updated:
            'Als je een nieuwe versie van een verstuurd rapport vaststelt en kiest voor een bericht aan de klant. Dezelfde link toont dan de nieuwe versie. Alleen per e-mail.',
        report_updated_with_invoice:
            'In plaats van Rapport bijgewerkt als er een factuur meegaat die de klant nog niet had, bijvoorbeeld een nieuwe na een creditering. Alleen per e-mail en alleen aan de klant, met de factuur als pdf-bijlage en je eigen bericht.',
        invoice_sent: 'Als een factuur naar de klant gaat, met een link naar de pdf.',
    } as Record<string, string>,

    /*
     * De stand van twee schakelaars die onder Automatisering staan (tabblad Rapport),
     * met dezelfde eerste zin als daar en een link erheen.
     */
    reminders: 'Herinneringen versturen',
    remindersOn: (days: number) => `Aan: de klant krijgt ${reminderMoment(days)} een herinnering.`,
    remindersOff,
    invoiceWithReport: 'Factuur automatisch meesturen met het rapport',
    invoiceWithReportState,
    setAtBefore: 'Aan- of uitzetten doe je bij ',
    setAtLink: 'Rapport → Automatisering',
    setAtAfter: '.',

    /* Het eigen bericht bij de factuur */
    invoiceMessage: 'Standaard eigen bericht',
    invoiceMessageHint:
        'Staat in de mail met het rapport en de factuur, onder de factuur. Bij versturen pas je het per keer aan. Leeg: geen eigen bericht.',
    invoiceMessagePlaceholder: 'Bijvoorbeeld: Hartelijk dank voor uw opdracht.',
    invoiceMessageSave: 'Bericht opslaan',
    invoiceMessageSaved: 'Opgeslagen. Het volgende rapport met factuur begint met dit bericht.',

    /* De editor */
    eyebrow: 'Bericht aan klanten',
    close: 'Sluiten',
    channelTabs: 'Kanaal',
    unsaved: 'niet opgeslagen',
    tabInvalid: 'klopt nog niet',
    statusStandard: 'Je gebruikt de standaardtekst.',
    statusCustomised: (date: string | null) =>
        date === null
            ? 'Je gebruikt een eigen tekst.'
            : `Je gebruikt een eigen tekst, aangepast op ${date}.`,
    statusDraft: 'Niet opgeslagen: klanten krijgen nog de vorige tekst.',
    showStandard: 'Standaardtekst bekijken',
    subject: 'Onderwerp',
    body: 'Tekst',
    bodyHintMail:
        'Schrijf elke alinea op één regel; een witregel begint een nieuwe alinea. Een link op een eigen regel wordt een knop.',
    bodyHintSms: 'Een sms heeft geen onderwerp. De naam van je bedrijf zet je er zelf in.',
    variables: 'Variabelen invoegen',
    variablesHint:
        'Klik om in te voegen waar de cursor staat. Bij het versturen staat er de echte waarde.',
    contactHintMail:
        'Contactgegevens wordt ‘via’ met het telefoonnummer en e-mailadres van je bedrijf, of niets als die er niet zijn. Zet het daarom direct achter een woord, zonder spatie.',
    contactHintSms:
        'Contactgegevens wordt in een sms ‘via’ met het telefoonnummer van je bedrijf, of niets zonder telefoonnummer. Zet het daarom direct achter een woord, zonder spatie.',
    paymentHint:
        'Rekeninggegevens wordt ‘op rekening’ met het IBAN en de naam van je bedrijf, of niets zonder IBAN. Zet het daarom direct achter een woord, zonder spatie.',
    personalMessageHint:
        'Eigen bericht wordt de tekst die je bij het versturen meegeeft, of je standaard eigen bericht. Zet het op een eigen regel: zonder bericht valt die regel weg.',
    insert: (label: string) => `${label} invoegen`,
    refused: (placeholders: string[]) =>
        `Dit bericht kent ${list(placeholders)} niet. Gebruik de variabelen hieronder.`,
    subjectRequired: 'Een e-mail heeft een onderwerp nodig.',
    bodyRequired: 'Schrijf een tekst.',
    tooLong: (max: number) => `Te lang: dit bericht mag maximaal ${max} tekens bevatten.`,
    characters: (count: number, max: number) => `${count} van ${max} tekens`,
    smsParts: (characters: number, parts: number) =>
        `Met voorbeeldgegevens ${characters} tekens: ${parts} ${parts === 1 ? 'sms' : 'sms-berichten'}.`,
    smsUnicode:
        'Er staat een teken in dat niet in een gewone sms past, zoals ë of een emoji. Dan passen er 70 tekens in één sms in plaats van 160.',

    previewTitle: 'Voorbeeld',
    previewIntro: 'Met voorbeeldgegevens, zoals de klant het krijgt.',
    previewLoading: 'Voorbeeld laden',
    previewUpdating: 'Bijwerken…',
    previewWaiting: 'Het voorbeeld verschijnt zodra de tekst klopt.',
    previewFrame: (label: string) => `Voorbeeld van de e-mail ${label}`,
    previewSubject: 'Onderwerp',
    previewFrom: 'Van',

    save: 'Opslaan',
    saving: 'Bezig met opslaan…',
    saved: 'Opgeslagen. Klanten krijgen vanaf nu deze tekst.',
    nothingToSave: 'Er is niets gewijzigd.',
    restore: 'Standaardtekst herstellen',
    restoreLabel: (channel: string) => `Bevestig herstellen van de standaardtekst voor ${channel}`,
    restoreBody: (channel: string) =>
        `Je eigen tekst voor ${channel} verdwijnt. Klanten krijgen weer de standaardtekst van Smartkeur.`,
    restoreYes: 'Ja, standaardtekst herstellen',
    restoring: 'Bezig met herstellen…',
    restored: 'De standaardtekst staat weer terug.',
    cancel: 'Annuleren',

    testMail: 'Stuur een testmail naar mijzelf',
    testMailSending: 'Bezig met versturen…',
    testMailHint:
        'Met voorbeeldgegevens, ook als je nog niet hebt opgeslagen. De knoppen in de testmail openen geen echt rapport.',
    testMailSent: (recipient: string) => `Testmail verstuurd naar ${recipient}.`,
    testMailLimited: (minutes: number) =>
        `Je hebt net al een paar testmails verstuurd. Probeer het over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'} opnieuw.`,

    discardTitle: 'Niet opgeslagen wijzigingen',
    discardBody:
        'Je hebt wijzigingen die nog niet zijn opgeslagen. Die gaan verloren als je sluit.',
    discardYes: 'Wijzigingen weggooien',
    discardNo: 'Verder bewerken',
} as const;

/** `a`, `a en b`, `a, b en c`. */
function list(items: readonly string[]): string {
    if (items.length <= 1) {
        return items.join('');
    }

    return `${items.slice(0, -1).join(', ')} en ${items[items.length - 1]}`;
}
